/**
 * What a parent's day looks like from the two things the portal actually knows: what a
 * teacher recorded, and when a program is scheduled to meet.
 *
 * The split between those two matters more than it looks. A recorded session is a fact —
 * somebody tapped a button and the row exists. A scheduled session is an inference, and the
 * portal has been wrong about it before: Flex families were once offered check-in on days
 * they had not bought, enrollments were treated as running before their term started, and
 * BHCS closure days looked like ordinary ones. So `buildTodaySessions` reports only what was
 * recorded, and `getNextSession` — which does infer — is kept to a single forward-looking
 * line that promises nothing about attendance.
 */

import type { AttendanceRecord } from '../types/attendance';
import { programLocationName, type WithProgramLocation } from '../types/location';
import { attendanceModeFor, type AttendanceMode } from './attendanceMode';
import {
  isClosedForProgramType,
  isDaySelectionBilling,
  isNonTeachingProgramType,
} from './programCalendar';
import { DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../config/brands';

/**
 * The closure calendar a programme keeps. A missing or unrecognized brand reads as the
 * default, which is what the column's own DEFAULT would have given the row.
 */
function brandOf(program: { brand_key?: string | null } | null | undefined): BrandKey {
  return isBrandKey(program?.brand_key) ? program.brand_key : DEFAULT_BRAND_KEY;
}
import {
  formatSessionTimeRange,
  getSessionWeekdays,
  getTodaySessionDate,
  sessionStartMinutes,
  shiftDateString,
  weekdayOfDateString,
  currentBusinessMinutesOfDay,
} from './schedule';

/**
 * The little a *recorded* session needs to know about its program.
 *
 * No term dates, deliberately. A record exists because a teacher tapped a button, and a row
 * that somehow falls outside its enrollment's dates is still a thing that happened — filtering
 * it out here would hide a real check-in behind a data-entry mistake.
 */
export interface TodaySessionProgram extends WithProgramLocation {
  name: string;
  schedule_cron: string;
  duration: number;
}

/**
 * What a *scheduled* session needs on top of that.
 *
 * The term dates are required rather than optional because they are the only thing stopping
 * `getNextSession` from announcing a class in a term that ended in December.
 */
export interface NextSessionProgram extends TodaySessionProgram {
  start_date: string;
  end_date: string;
  /**
   * Optional, and load-bearing when present: for `per_day` and `flex_monthly` the cron lists
   * the days a family *may* choose, not the days they bought. Treating those as scheduled is
   * the original sin this file was written to prevent — parents were once offered check-in on
   * Flex days they had never paid for.
   */
  billing_type?: string | null;
  /**
   * Days this programme alone does not meet, beyond the school-wide calendar. Honoured for
   * every billing type: a vendor day off is not a day the family could have bought.
   */
  excluded_dates?: string[] | null;
  /**
   * Which brand's closure calendar this programme keeps. Optional because rows selected
   * before the column existed omit it; absent reads as the default brand, matching the
   * column's own DEFAULT. Without it a Kaleido class is cancelled by a BHCS holiday.
   */
  brand_key?: string | null;
  /**
   * `programs.type`, which decides *which* closure calendar applies — enrichment classes
   * are cancelled by a morning camp or Halloween that leaves after-school running.
   *
   * Optional, and absent reads as after-school. That is the conservative default: it can
   * only ever hide a day the school is genuinely shut, whereas guessing the other way would
   * silently drop ten days on which after-school does run.
   */
  type?: string | null;
}

/** An active enrollment, as both parent surfaces already have it in hand. */
export interface TodaySessionEnrollment {
  id: string;
  programs: TodaySessionProgram | null;
}

/** An active enrollment that also knows its term, for the forward-looking line. */
export interface NextSessionEnrollment {
  id: string;
  programs: NextSessionProgram | null;
  /**
   * The days this family actually chose, for programs that are billed by the day —
   * `metadata.selected_days` for `per_day`, the month's flex plan for `flex_monthly`.
   *
   * Required for those two and ignored for everything else. Null or missing means no day is
   * confirmed, which is a real answer: a Flex month the parent has not planned holds no
   * sessions, and the card says nothing rather than inventing one.
   */
  selectedDays?: string[] | null;
}

/** One session a teacher has recorded something about today. */
export interface TodaySession {
  enrollmentId: string;
  programName: string;
  /** Where it meets, or null when no location is on file. */
  locationName: string | null;
  /**
   * Minutes past midnight the program is scheduled to start, or `Infinity` when its cron
   * carries no readable time. Exposed so a caller can interleave recorded sessions with
   * scheduled ones in the order the day actually runs.
   */
  startMinutes: number;
  /** `12:00 PM – 6:00 PM`, or null when the cron carries no readable time. */
  timeRange: string | null;
  /**
   * Whether this session ends by releasing the child to a named adult.
   *
   * Carried through rather than re-derived at render, because it decides which sentences a
   * row is allowed to say once `parentPickupDetails` is on: a `class` session may report an
   * arrival, never a handover. With the flag off nothing reads it, and nothing may — the
   * parent surfaces say the same thing about both kinds of program.
   */
  mode: AttendanceMode;
  record: AttendanceRecord;
}

/** The next time a program meets, inferred from its schedule alone. */
export interface NextSession {
  programName: string;
  locationName: string | null;
  /** `YYYY-MM-DD` in the business timezone. */
  date: string;
  timeRange: string | null;
}

/**
 * Today's recorded sessions, in the order the day runs.
 *
 * Three filters, each load-bearing:
 *
 * - the record must belong to one of the enrollments passed in, so a stale row for a
 *   withdrawn seat cannot surface;
 * - `not_arrived` is dropped, because it is the state a roster row is born in and cannot be
 *   told apart from "the teacher has not tapped yet" — rendering it would tell a parent
 *   "not arrived" about a child who has been in the room since three o'clock.
 *
 * Enrichment classes are *kept*, which reverses what the Students page used to do. The old
 * rule dropped them wholesale on the grounds that "picked up" names no adult there — true,
 * but it threw out the arrival with the handover. "Woody checked in to Coding for Kids at
 * 3:03 PM" is a fact, and on a card whose whole job is "where is my child right now" it is
 * one of the facts being asked for. The objection that a check-in with no check-out reads as
 * a child still waiting hours later is answered by the time range printed beside it: the row
 * says the class ran 3:00–4:30 PM, so 6 o'clock does not look like custody.
 *
 * What stays suppressed is the claim itself — see `mode`, which the pill reads to decide
 * whether a handover may be named at all, and which matters only while `parentPickupDetails`
 * is on. Off, its default, the parent surfaces report the arrival and never the check-out.
 *
 * Sorted by when the program starts rather than by when it was recorded, so the card reads
 * as the shape of the day rather than the order the teachers happened to tap.
 */
export function buildTodaySessions(
  enrollments: readonly TodaySessionEnrollment[],
  records: readonly AttendanceRecord[],
): TodaySession[] {
  const byEnrollment = new Map(enrollments.map(e => [e.id, e]));

  const ordered = records
    .filter(r => byEnrollment.has(r.enrollmentId))
    .filter(r => r.status !== 'not_arrived')
    .map(record => {
      const program = byEnrollment.get(record.enrollmentId)?.programs ?? null;
      return {
        // A cron with no readable start sorts last rather than to midnight, so bad data
        // cannot push itself to the top of the parent's day.
        startMinutes: program ? sessionStartMinutes(program.schedule_cron) ?? Infinity : Infinity,
        session: {
          enrollmentId: record.enrollmentId,
          programName: program?.name ?? 'Program',
          locationName: programLocationName(program),
          startMinutes: program ? sessionStartMinutes(program.schedule_cron) ?? Infinity : Infinity,
          timeRange: program ? formatSessionTimeRange(program.schedule_cron, program.duration) : null,
          mode: attendanceModeFor(record.programType),
          record,
        },
      };
    })
    .sort((a, b) => a.startMinutes - b.startMinutes);

  return ordered.map(entry => entry.session);
}

/**
 * How far ahead `getNextSession` will look before giving up.
 *
 * Longer than a school year on purpose. A family enrolled only in a spring program is looking
 * at a five-month gap in August, and "nothing scheduled" would be a worse answer than the real
 * date — the walk stops at each program's own `end_date` anyway, so the bound is only ever
 * reached by a genuinely distant term.
 */
const LOOKAHEAD_DAYS = 400;

/**
 * The soonest meeting across these enrollments, or null when none is coming.
 *
 * A day-by-day walk in `YYYY-MM-DD` rather than instant arithmetic: every input here is a
 * business-timezone calendar day or wall clock, and converting any of it to an instant is
 * how a Friday class starts announcing itself as Thursday to a parent on the east coast.
 *
 * Today counts only until the class starts. After that the answer is the *next* one — a
 * parent reading "next class 4:00 PM" at half past four is being told something false about
 * a class already in progress.
 *
 * This is an inference, not a record: it says when a program is *scheduled* to meet, never
 * that anyone attended. That is why it renders as a quiet schedule line and never as an
 * attendance claim.
 *
 * It does skip days that are known not to happen — a school closure for the calendar this
 * program keeps, and a Flex day the family did not buy. Both were once allowed through, on
 * the reasoning that a schedule line promises nothing; but the line names a weekday and a
 * time, and "next class Thu Nov 26" on Thanksgiving is simply false.
 */
export function getNextSession(
  enrollments: readonly NextSessionEnrollment[],
  today: string = getTodaySessionDate(),
  nowMinutes: number = currentBusinessMinutesOfDay(),
): NextSession | null {
  let best: NextSession | null = null;
  /** Minutes from today's midnight, so sessions on different days compare directly. */
  let bestRank = Infinity;

  for (const enrollment of enrollments) {
    const program = enrollment.programs;
    if (!program?.schedule_cron) continue;
    if (isNonTeachingProgramType(program.type)) continue;

    // Validated before anything else reads the cron. `getSessionWeekdays` throws outright on a
    // malformed one, and a start time is what decides whether today still counts — so a cron
    // this cannot read is skipped rather than guessed at in either direction.
    const startMinutes = sessionStartMinutes(program.schedule_cron);
    if (startMinutes === null) continue;

    const weekdays = getSessionWeekdays(program.schedule_cron);
    if (weekdays.size === 0) continue;

    // The same rule the rest of this file follows: for a family that picks its own days, the
    // cron is a list of candidates and their plan is the answer. A day outside the planned
    // month is not in it, so a Flex enrollment simply stops looking ahead — which is the
    // truthful outcome, since nobody has said yet whether that day is happening.
    const byPlan = isDaySelectionBilling(program.billing_type);

    for (let offset = 0; offset <= LOOKAHEAD_DAYS; offset++) {
      const day = shiftDateString(today, offset);
      if (day > program.end_date) break;
      if (day < program.start_date) continue;
      if (!weekdays.has(weekdayOfDateString(day))) continue;
      if (byPlan && !enrollment.selectedDays?.includes(day)) continue;
      if (program.excluded_dates?.includes(day)) continue;
      // Same calendar `meetsToday` consults, for the same reason: a line reading "next
      // class Thursday" about Thanksgiving is worse than no line, and an enrichment class
      // must not be promised on a Fall Camp afternoon it is cancelled for.
      if (!byPlan && isClosedForProgramType(program.type, day, brandOf(program))) continue;
      if (offset === 0 && startMinutes <= nowMinutes) continue;

      const rank = offset * 24 * 60 + startMinutes;
      if (rank < bestRank) {
        bestRank = rank;
        best = {
          programName: program.name,
          locationName: programLocationName(program),
          date: day,
          timeRange: formatSessionTimeRange(program.schedule_cron, program.duration),
        };
      }
      break;
    }
  }

  return best;
}

/** A class the timetable says meets today, with nothing recorded against it. */
export interface ScheduledSession {
  enrollmentId: string;
  programName: string;
  locationName: string | null;
  /** `12:00 PM – 6:00 PM`, or null when the cron carries no readable time. */
  timeRange: string | null;
  startMinutes: number;
  /** Whether its start time has passed. Decides whether it is announced or merely stated. */
  hasStarted: boolean;
}

/** Whether this enrollment genuinely meets today. One rule, one place. */
function meetsToday(
  enrollment: NextSessionEnrollment,
  today: string,
  blackouts?: ReadonlySet<string>,
): boolean {
  const program = enrollment.programs;
  if (!program?.schedule_cron) return false;
  // A waitlist holds no sessions; its cron is a placeholder, not a schedule.
  if (isNonTeachingProgramType(program.type)) return false;
  // Readability gate before anything reads the cron for meaning: `getSessionWeekdays` throws
  // outright on a malformed one, and a card that white-screens is worse than a missing line.
  if (sessionStartMinutes(program.schedule_cron) === null) return false;
  if (today < program.start_date || today > program.end_date) return false;

  // A vendor day off outranks even a bought day: the class is not running.
  if (program.excluded_dates?.includes(today)) return false;

  // A family that picks its own days has already answered this question, and their answer
  // is the only one that counts — a skipped Flex day is skipped, whatever the cron says.
  if (isDaySelectionBilling(program.billing_type)) {
    return enrollment.selectedDays?.includes(today) ?? false;
  }

  // Everyone else meets on the weekdays their cron names, minus the days their own calendar
  // is shut. Which calendar that is depends on the program: `blackoutDates.ts` makes a Labor
  // Day unbookable for after-school, and separately cancels every enrichment class on a Fall
  // Camp afternoon that after-school works straight through.
  if (isClosedToday(enrollment, today, blackouts)) return false;
  return getSessionWeekdays(program.schedule_cron).has(weekdayOfDateString(today));
}

/**
 * Whether the school-wide calendar closes this enrollment on `ymd`.
 *
 * `override` replaces the calendar outright, for both kinds of program — it exists so a test
 * can state "nothing is closed" or "this one day is" without depending on the real BHCS
 * year. Left out, each program asks the calendar its own type keeps.
 */
function isClosedToday(
  enrollment: NextSessionEnrollment,
  ymd: string,
  override?: ReadonlySet<string>,
): boolean {
  if (override) return override.has(ymd);
  return isClosedForProgramType(enrollment.programs?.type, ymd, brandOf(enrollment.programs));
}

/**
 * Every class these enrollments genuinely have today, earliest first.
 *
 * The card used to show a scheduled class only while it was still ahead, which meant a child
 * with no record vanished from the day the moment their class began — a parent opening the
 * portal at four saw an empty row for a child who had been in after-school since noon, with
 * no way to tell that from "no class today". Whether a teacher has tapped a button is not
 * something the timetable knows, and the timetable is what this reports.
 *
 * It says nothing whatsoever about the child. A returned session means "this class meets
 * today"; the caller renders it in grey, with no pill, and only a recorded row earns one.
 */
export function getTodayScheduledSessions(
  enrollments: readonly NextSessionEnrollment[],
  today: string = getTodaySessionDate(),
  nowMinutes: number = currentBusinessMinutesOfDay(),
  blackouts?: ReadonlySet<string>,
): ScheduledSession[] {
  const found: ScheduledSession[] = [];

  for (const enrollment of enrollments) {
    if (!meetsToday(enrollment, today, blackouts)) continue;

    // Read after `meetsToday`, which has already established the cron is one this file can
    // parse; an unreadable one is skipped rather than guessed at in either direction.
    const program = enrollment.programs;
    const startMinutes = program ? sessionStartMinutes(program.schedule_cron) : null;
    if (!program || startMinutes === null) continue;

    found.push({
      enrollmentId: enrollment.id,
      programName: program.name,
      locationName: programLocationName(program),
      timeRange: formatSessionTimeRange(program.schedule_cron, program.duration),
      startMinutes,
      hasStarted: startMinutes <= nowMinutes,
    });
  }

  return found.sort((a, b) => a.startMinutes - b.startMinutes);
}
